import { request } from '../api/http';
import type { TokenPair } from '../api/types';

/*
 * The signed-in session. The access token lives in this module's memory only,
 * so a reload loses it and the refresh token (sessionStorage, gone when the tab
 * closes) gets a new one. Nothing is ever written to localStorage.
 */

const REFRESH_TOKEN_KEY = 'staff.refresh_token';
/** Refresh this long before the access token expires (or halfway, for a short token). */
const REFRESH_LEAD_SECONDS = 60;

const listeners = new Set<() => void>();
let accessToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let refreshing: Promise<boolean> | null = null;

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const getAccessToken = () => accessToken;

export const hasStoredRefreshToken = () =>
  sessionStorage.getItem(REFRESH_TOKEN_KEY) !== null;

/** Keeps a new token pair and schedules the refresh. */
export function startSession(tokens: TokenPair) {
  accessToken = tokens.access_token;
  sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  clearTimeout(refreshTimer);
  const lead = Math.min(REFRESH_LEAD_SECONDS, tokens.expires_in / 2);
  refreshTimer = setTimeout(
    () => {
      void refreshSession();
    },
    (tokens.expires_in - lead) * 1000,
  );
  notify();
}

/** Drops both tokens; the app shows the login screen. */
export function endSession() {
  accessToken = null;
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  clearTimeout(refreshTimer);
  notify();
}

/** Exchanges the refresh token for a new pair. Any failure ends the session. */
export function refreshSession(): Promise<boolean> {
  const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    endSession();
    return Promise.resolve(false);
  }
  refreshing ??= request<TokenPair>('/v1/staff/auth/refresh', {
    method: 'POST',
    body: { refresh_token: refreshToken },
  })
    .then(
      tokens => {
        startSession(tokens);
        return true;
      },
      () => {
        endSession();
        return false;
      },
    )
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}
